import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiFinanceManualService } from '../../../core/services/api/finance/api-finance-manual.service';
import { MapperFinanceManualListService } from '../../../core/mappers/finance/mapper-finance-manual-list.service';
import { FinanceAttachment } from '../../../core/models/finance/finance-manual-list.model';

/** แสดงไฟล์แนบของ parking_finance (city_parking_finance_attachment) พร้อม preview */
@Component({
  selector: 'app-finance-attachments',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 p-5 shadow-sm">
      <h4 class="text-sm font-bold text-slate-700 dark:text-slate-200 mb-4 flex items-center gap-2">
        <span class="w-1 h-5 bg-amber-400 rounded-full"></span>
        {{ title }}
      </h4>

      <div *ngIf="isLoading" class="py-8 flex justify-center text-sm text-slate-400">กำลังโหลด...</div>

      <div *ngIf="!isLoading && !attachments.length"
        class="py-8 flex flex-col items-center text-slate-400 dark:text-slate-500 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
        <span class="material-icons text-3xl mb-1">image_not_supported</span>
        <span class="text-sm">ไม่มีรูปแนบ</span>
      </div>

      <div *ngIf="!isLoading && attachments.length" class="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <button type="button" *ngFor="let file of attachments" (click)="previewUrl = file.fileUrl"
          class="group relative aspect-[3/4] rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 hover:border-amber-400 transition-colors">
          <img [src]="file.fileUrl" [alt]="file.fileName || title" loading="lazy" class="h-full w-full object-contain p-1" />
          <span class="absolute inset-x-0 bottom-0 px-2 py-1 text-[11px] text-white bg-slate-900/60 truncate text-left">
            {{ purposeLabel(file.purpose) }}<ng-container *ngIf="file.fileName"> · {{ file.fileName }}</ng-container>
          </span>
        </button>
      </div>
    </div>

    <div #previewOverlay *ngIf="previewUrl" class="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/80"
      (click)="previewUrl = null">
      <div class="absolute top-4 right-4 flex gap-2">
        <a [href]="previewUrl" target="_blank" rel="noopener" (click)="$event.stopPropagation()"
          class="w-10 h-10 rounded-xl bg-white/90 text-slate-700 hover:text-amber-600 flex items-center justify-center"
          title="เปิดในแท็บใหม่">
          <span class="material-icons text-[20px]">open_in_new</span>
        </a>
        <button type="button" (click)="previewUrl = null"
          class="w-10 h-10 rounded-xl bg-white/90 text-slate-700 hover:text-amber-600 flex items-center justify-center">
          <span class="material-icons text-[20px]">close</span>
        </button>
      </div>
      <img [src]="previewUrl" [alt]="title" class="max-h-[90vh] max-w-full rounded-xl shadow-2xl bg-white"
        (click)="$event.stopPropagation()" />
    </div>
  `,
})
export class FinanceAttachmentsComponent implements OnChanges {
  @Input() financeId: number | null | undefined = 0;
  @Input() siteId: number | null | undefined = 0;
  @Input() userId: number | null | undefined = 0;
  @Input() title = 'หลักฐานการชำระเงิน';

  private readonly api = inject(ApiFinanceManualService);
  private readonly mapper = inject(MapperFinanceManualListService);

  attachments: FinanceAttachment[] = [];
  isLoading = false;
  previewUrl: string | null = null;

  /** Parent modals use transform animations, which would trap position:fixed inside the card. */
  @ViewChild('previewOverlay') set previewOverlay(ref: ElementRef<HTMLElement> | undefined) {
    if (ref) document.body.appendChild(ref.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['financeId'] || changes['siteId'] || changes['userId']) {
      this.load();
    }
  }

  purposeLabel(purpose: string): string {
    switch (purpose) {
      case 'transfer_slip':
        return 'หลักฐานการโอน';
      case 'receipt':
        return 'รูปใบเสร็จ';
      default:
        return 'ไฟล์แนบ';
    }
  }

  private load(): void {
    const financeId = Number(this.financeId) || 0;
    const siteId = Number(this.siteId) || 0;
    const userId = Number(this.userId) || 0;
    this.attachments = [];
    if (financeId <= 0 || siteId <= 0 || userId <= 0) return;

    this.isLoading = true;
    this.api.getFinanceAttachments(financeId, siteId, userId).subscribe({
      next: (res) => {
        this.attachments = this.mapper.mapAttachments(res);
        this.isLoading = false;
      },
      error: () => {
        this.attachments = [];
        this.isLoading = false;
      },
    });
  }
}
